// Super-admin shared to-do list.
// /admin/todos
//
// Lightweight team checklist. Open items live at the top, completed
// items get crossed out and time-stamped. Anyone with super-admin role
// can edit and check off — done_by is recorded so the team can see who
// did what.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Plus, Loader2, Check, Trash2, Pencil, ListTodo,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import TodoNotificationSettings from './TodoNotificationSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const PRIORITY_PILL = {
  low: { label: 'Low', cls: 'bg-stone-100 text-stone-600' },
  normal: { label: 'Normal', cls: 'bg-[#7C6F9C]/10 text-[#7C6F9C]' },
  high: { label: 'High', cls: 'bg-rose-100 text-rose-700' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

export default function AdminTodos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todos, setTodos] = useState([]);
  const [stats, setStats] = useState({ open_count: 0, done_count: 0 });
  const [newText, setNewText] = useState('');
  const [newPriority, setNewPriority] = useState('normal');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ text: '', notes: '', priority: 'normal' });
  const [showDone, setShowDone] = useState(false);

  const auth = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const fetchTodos = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/todos`, auth());
      setTodos(r.data?.todos || []);
      setStats({ open_count: r.data?.open_count || 0, done_count: r.data?.done_count || 0 });
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Super-admin only');
        navigate('/');
      } else {
        toast.error("Couldn't load to-do list");
      }
    } finally {
      setLoading(false);
    }
  }, [auth, navigate]);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const text = (newText || '').trim();
    if (!text) { toast.error('Enter a task first'); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/admin/todos`, { text, priority: newPriority }, auth());
      setTodos((arr) => [r.data, ...arr]);
      setStats((s) => ({ ...s, open_count: s.open_count + 1 }));
      setNewText('');
      setNewPriority('normal');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add task');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (todo) => {
    try {
      const r = await axios.post(`${API}/admin/todos/${todo.id}/toggle`, {}, auth());
      const next = r.data;
      setTodos((arr) => arr.map((t) => (t.id === todo.id ? { ...t, ...next } : t)));
      setStats((s) => ({
        open_count: s.open_count + (next.done ? -1 : 1),
        done_count: s.done_count + (next.done ? 1 : -1),
      }));
    } catch {
      toast.error('Toggle failed');
    }
  };

  const handleDelete = async (todo) => {
    if (!window.confirm(`Delete "${todo.text}"?`)) return;
    try {
      await axios.delete(`${API}/admin/todos/${todo.id}`, auth());
      setTodos((arr) => arr.filter((t) => t.id !== todo.id));
      setStats((s) => ({
        open_count: todo.done ? s.open_count : Math.max(0, s.open_count - 1),
        done_count: todo.done ? Math.max(0, s.done_count - 1) : s.done_count,
      }));
    } catch {
      toast.error('Delete failed');
    }
  };

  const startEdit = (todo) => {
    setEditingId(todo.id);
    setEditDraft({
      text: todo.text || '',
      notes: todo.notes || '',
      priority: todo.priority || 'normal',
    });
  };

  const saveEdit = async (todo) => {
    try {
      const r = await axios.put(`${API}/admin/todos/${todo.id}`, editDraft, auth());
      setTodos((arr) => arr.map((t) => (t.id === todo.id ? { ...t, ...r.data } : t)));
      setEditingId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const { open, done } = useMemo(() => ({
    open: todos.filter((t) => !t.done),
    done: todos.filter((t) => t.done),
  }), [todos]);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-todos-page">
      <header className="max-w-3xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-900"
          data-testid="todos-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Link to="/" className="text-sm text-stone-500 hover:underline">PlayFirst</Link>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 pb-16">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-[#7C6F9C]/10 text-[#7C6F9C] flex items-center justify-center">
              <ListTodo className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-serif text-stone-900">Team to-do list</h1>
              <p className="text-sm text-stone-500">
                Shared with the PlayFirst team — anyone with super-admin access can tick things off.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 gap-3 mt-6">
          <Stat label="Open" value={stats.open_count} testid="todos-stat-open" />
          <Stat label="Completed" value={stats.done_count} testid="todos-stat-done" />
        </div>

        {/* WhatsApp notification numbers (super-admin) */}
        <div className="mt-6">
          <TodoNotificationSettings />
        </div>

        {/* Add new task */}
        <form onSubmit={handleCreate} className="mt-6 rounded-2xl border border-stone-200 bg-white p-4" data-testid="todos-add-form">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              className="flex-1"
              placeholder="What needs doing?"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              data-testid="todos-new-input"
            />
            <select
              value={newPriority}
              onChange={(e) => setNewPriority(e.target.value)}
              className="h-10 px-3 rounded-md border border-stone-200 bg-white text-sm"
              data-testid="todos-new-priority"
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
            <Button
              type="submit"
              disabled={creating}
              className="bg-[#7C6F9C] hover:bg-[#6a5d8a]"
              data-testid="todos-add-submit"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {creating ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </form>

        {/* Refresh + filter */}
        <div className="mt-4 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTodos}
            disabled={loading}
            data-testid="todos-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {done.length > 0 && (
            <button
              type="button"
              onClick={() => setShowDone(!showDone)}
              className="text-sm text-stone-500 hover:text-stone-900"
              data-testid="todos-toggle-done"
            >
              {showDone ? 'Hide' : 'Show'} {done.length} completed
            </button>
          )}
        </div>

        {/* Open items */}
        <section className="mt-4 space-y-2" data-testid="todos-list-open">
          {loading && (
            <div className="text-center py-10 text-stone-500">
              <Loader2 className="w-5 h-5 inline animate-spin" /> Loading…
            </div>
          )}
          {!loading && open.length === 0 && (
            <div className="text-center py-10 text-stone-500">
              All caught up. 💛
            </div>
          )}
          <AnimatePresence initial={false}>
            {open.map((todo) => (
              <TodoRow
                key={todo.id}
                todo={todo}
                editing={editingId === todo.id}
                editDraft={editDraft}
                setEditDraft={setEditDraft}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onStartEdit={startEdit}
                onSaveEdit={saveEdit}
                onCancelEdit={() => setEditingId(null)}
              />
            ))}
          </AnimatePresence>
        </section>

        {/* Done items */}
        {showDone && done.length > 0 && (
          <section className="mt-6 space-y-2" data-testid="todos-list-done">
            <h3 className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Completed</h3>
            {done.slice(0, 50).map((todo) => (
              <TodoRow
                key={todo.id}
                todo={todo}
                editing={false}
                editDraft={editDraft}
                setEditDraft={setEditDraft}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onStartEdit={() => {}}
                onSaveEdit={() => {}}
                onCancelEdit={() => {}}
                readOnlyControls
              />
            ))}
          </section>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</p>
      <p className="text-2xl font-serif text-stone-900 mt-1">{value ?? 0}</p>
    </div>
  );
}

function TodoRow({
  todo, editing, editDraft, setEditDraft, onToggle, onDelete,
  onStartEdit, onSaveEdit, onCancelEdit, readOnlyControls,
}) {
  const pill = PRIORITY_PILL[todo.priority] || PRIORITY_PILL.normal;

  if (editing) {
    return (
      <div className="rounded-2xl border border-[#7C6F9C]/40 bg-white p-3 space-y-2" data-testid={`todo-row-${todo.id}`}>
        <Input
          value={editDraft.text}
          onChange={(e) => setEditDraft((d) => ({ ...d, text: e.target.value }))}
          data-testid={`todo-edit-text-${todo.id}`}
        />
        <Textarea
          value={editDraft.notes}
          onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))}
          placeholder="Notes (optional)"
          rows={2}
          data-testid={`todo-edit-notes-${todo.id}`}
        />
        <div className="flex items-center gap-2 justify-end">
          <select
            value={editDraft.priority}
            onChange={(e) => setEditDraft((d) => ({ ...d, priority: e.target.value }))}
            className="h-8 px-2 rounded border border-stone-200 text-sm"
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
          <Button size="sm" variant="ghost" onClick={onCancelEdit} data-testid={`todo-edit-cancel-${todo.id}`}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => onSaveEdit(todo)} data-testid={`todo-edit-save-${todo.id}`}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.15 }}
      className={`rounded-2xl border border-stone-200 bg-white p-3 flex items-start gap-3 group ${
        todo.done ? 'opacity-60' : ''
      }`}
      data-testid={`todo-row-${todo.id}`}
    >
      <button
        type="button"
        onClick={() => onToggle(todo)}
        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors mt-0.5 ${
          todo.done
            ? 'bg-emerald-500 border-emerald-500 text-white'
            : 'border-stone-300 hover:border-[#7C6F9C]'
        }`}
        aria-label={todo.done ? 'Mark incomplete' : 'Mark complete'}
        data-testid={`todo-toggle-${todo.id}`}
      >
        {todo.done && <Check className="w-3.5 h-3.5" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`text-sm ${todo.done ? 'line-through text-stone-500' : 'text-stone-900'}`}>
            {todo.text}
          </p>
          {!todo.done && (
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${pill.cls}`}>
              {pill.label}
            </span>
          )}
        </div>
        {todo.notes && (
          <p className="text-xs text-stone-500 mt-1 whitespace-pre-line">{todo.notes}</p>
        )}
        <p className="text-[11px] text-stone-400 mt-1">
          Added by {todo.created_by_email || 'team'} · {fmt(todo.created_at)}
          {todo.done && todo.done_by && (
            <> · ✓ {todo.done_by} {todo.done_at ? `· ${fmt(todo.done_at)}` : ''}</>
          )}
        </p>
      </div>

      {!readOnlyControls && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => onStartEdit(todo)}
            className="text-stone-400 hover:text-stone-700 p-1"
            aria-label="Edit"
            data-testid={`todo-edit-${todo.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(todo)}
            className="text-stone-400 hover:text-rose-600 p-1"
            aria-label="Delete"
            data-testid={`todo-delete-${todo.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {readOnlyControls && (
        <button
          type="button"
          onClick={() => onDelete(todo)}
          className="text-stone-400 hover:text-rose-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Delete"
          data-testid={`todo-delete-${todo.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </motion.div>
  );
}
